import React, { useState, useEffect, useCallback } from 'react';

const WordleRomanian = ({words, guessable}) => {
  // Baza de cuvinte în română (doar cuvinte de 5 literelumină', 'umbră', 'masă', 'scaun', 
  const wordBank = words;

  // State-uri pentru joc
  const [secretWordOriginal, setSecretWordOriginal] = useState('');
  const [secretWord, setSecretWord] = useState('');
  const [attempts, setAttempts] = useState([]);
  const [currentAttempt, setCurrentAttempt] = useState('');
  const [gameOver, setGameOver] = useState(false);
  const [message, setMessage] = useState('');
  const [keyboardStatus, setKeyboardStatus] = useState({});

  // Funcție pentru a începe un joc nou
  const startNewGame = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * guessable.length);
    const word = guessable[randomIndex];
    setSecretWordOriginal(word);
    const wordWithoutAccents = removeDiacritics(word);
    setSecretWord(wordWithoutAccents);
    setAttempts([]);
    setCurrentAttempt('');
    setGameOver(false);
    setMessage('Ghicește cuvântul de 5 litere!');
    setKeyboardStatus({});
  }, [guessable]);

    // Inițializare joc
    useEffect(() => {
      startNewGame();
    }, [startNewGame]);
  
  

  // Funcție pentru verificarea literei în cuvânt
  const checkLetter = (letter, position, word) => {
    if (word[position] === letter) {
      return 'correct'; // Litera corectă în poziția corectă
    } else if (word.includes(letter)) {
      return 'present'; // Litera este în cuvânt dar în poziție greșită
    } else {
      return 'absent'; // Litera nu este în cuvânt
    }
  };

  // Funcție pentru procesarea încercării
  const processAttempt = useCallback(() => {
    if (currentAttempt.length !== 5) {
      setMessage('Cuvântul trebuie să aibă 5 litere!');
      return;
    }

    if (!wordBank.includes(currentAttempt)) {
      setMessage('Cuvântul nu este în lista noastră!');
      return;
    }

    const newAttempt = {
      word: currentAttempt,
      result: Array.from(currentAttempt).map((letter, index) =>
        checkLetter(letter, index, secretWord)
      )
    };

    // Actualizare status tastatură
    const newKeyboardStatus = { ...keyboardStatus };
    Array.from(currentAttempt).forEach((letter, index) => {
      const status = checkLetter(letter, index, secretWord);
      // Prioritate: correct > present > absent
      if (newKeyboardStatus[letter] !== 'correct') {
        if (status === 'correct' || (status === 'present' && newKeyboardStatus[letter] !== 'present')) {
          newKeyboardStatus[letter] = status;
        } else if (!newKeyboardStatus[letter]) {
          newKeyboardStatus[letter] = status;
        }
      }
    });

    setKeyboardStatus(newKeyboardStatus);

    const newAttempts = [...attempts, newAttempt];
    setAttempts(newAttempts);

    if (currentAttempt === secretWord) {
      setGameOver(true);
      setMessage(`Felicitări! Ai ghicit cuvântul: ${secretWordOriginal}`);
    } else if (newAttempts.length >= 6) {
      setGameOver(true);
      setMessage(`Joc terminat! Cuvântul era: ${secretWordOriginal}`);
    }

    setCurrentAttempt('');
  }, [currentAttempt, secretWord, secretWordOriginal, attempts, wordBank, keyboardStatus]);


  // Adăugare event listener pentru tastatură
  useEffect(() => {
  // Handler pentru tastatura fizică
  const handleKeyDown = (e) => {
    if (gameOver) return;

    if (e.key === 'Enter') {
      processAttempt();
    } else if (e.key === 'Backspace') {
      setCurrentAttempt(prev => prev.slice(0, -1));
    } else if (/^[a-zA-Z]$/.test(e.key) && currentAttempt.length < 5) {
      setCurrentAttempt(prev => prev + e.key.toLowerCase());
    }
  };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentAttempt, gameOver, processAttempt]);

  // Handler pentru tastatura virtuală
  const handleVirtualKeyPress = (key) => {
    if (gameOver) return;

    if (key === 'ENTER') {
      processAttempt();
    } else if (key === '⌫') {
      setCurrentAttempt(prev => prev.slice(0, -1));
    } else if (currentAttempt.length < 5) {
      setCurrentAttempt(prev => prev + key.toLowerCase());
    }
  };

  // Randare tastatura virtuală
const renderKeyboard = () => {
    const rows = [
        ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
        ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
        ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '⌫']
    ];

    return (
        <div className="flex flex-col items-center w-full">
            {rows.map((row, rowIdx) => (
                <div key={rowIdx} className="flex my-0.5 w-full justify-center">
                    {row.map((key) => {
                        const status = keyboardStatus[key.toLowerCase()];
                        let bgColor = 'bg-gray-300';

                        if (status === 'correct') {
                            bgColor = 'bg-green-500';
                        } else if (status === 'present') {
                            bgColor = 'bg-yellow-500';
                        } else if (status === 'absent') {
                            bgColor = 'bg-gray-400';
                        }

                        // Ajustare dimensiuni butoane pentru a se potrivi mai bine pe mobil
                        let widthClass = 'w-9';  // Valoare implicită
                        let pxClass = 'px-1.5';
                        if (key === 'ENTER' || key === '⌫') {
                            widthClass = 'w-auto'; // Lățime automată pentru ENTER și ⌫
                            pxClass = 'px-3';
                        }

                        return (
                            <button
                                key={key}
                                className={`${bgColor} text-xs font-bold p-1.5 m-0.5 rounded ${widthClass} h-10 text-center ${pxClass}`}
                                onClick={() => handleVirtualKeyPress(key)}
                                whileTap={{ scale: 0.95 }} // Efect de apăsare
                            >
                                {key}
                            </button>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

  // Randare grilă de încercări
  const renderGrid = () => {
    const totalRows = 6;
    const rows = [];

    // Adăugare încercări anterioare
    for (let i = 0; i < attempts.length; i++) {
      rows.push(
        <div key={`attempt-${i}`} className="flex mb-1">
          {Array.from(attempts[i].word).map((letter, letterIdx) => {
            const status = attempts[i].result[letterIdx];
            let bgColor = 'bg-gray-200';

            if (status === 'correct') {
              bgColor = 'bg-green-500';
            } else if (status === 'present') {
              bgColor = 'bg-yellow-500';
            } else if (status === 'absent') {
              bgColor = 'bg-gray-400';
            }

            return (
              <div
                key={`letter-${i}-${letterIdx}`}
                className={`${bgColor} w-12 h-12 border border-gray-300 flex items-center justify-center font-bold text-lg uppercase`}
              >
                {letter}
              </div>
            );
          })}
        </div>
      );
    }

    // Adăugare încercare curentă
    if (!gameOver && attempts.length < totalRows) {
      const currentRow = [];

      // Adăugare litere din încercarea curentă
      for (let i = 0; i < 5; i++) {
        currentRow.push(
          <div
            key={`current-${i}`}
            className={`w-12 h-12 border border-gray-400 flex items-center justify-center font-bold text-lg uppercase ${i < currentAttempt.length ? 'border-black' : ''}`}
          >
            {i < currentAttempt.length ? currentAttempt[i] : ''}
          </div>
        );
      }

      rows.push(<div key="current-attempt" className="flex mb-1">{currentRow}</div>);
    }

    // Adăugare rânduri goale rămase
    const emptyRowsCount = totalRows - attempts.length - (gameOver ? 0 : 1);

    for (let i = 0; i < emptyRowsCount; i++) {
      const emptyRow = [];

      for (let j = 0; j < 5; j++) {
        emptyRow.push(
          <div
            key={`empty-${i}-${j}`}
            className="w-12 h-12 border border-gray-300 flex items-center justify-center"
          ></div>
        );
      }

      rows.push(<div key={`empty-row-${i}`} className="flex mb-1">{emptyRow}</div>);
    }

    return <div className="mb-4">{rows}</div>;
  };

  return (
    <div className="flex flex-col items-center p-4 max-w-md mx-auto">
      <h1 className="text-3xl font-bold mb-4">Vorble</h1>
      <div className="text-center mb-4">
        <p className="text-lg">{message}</p>
        {gameOver && (
          <button
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={startNewGame}
          >
            Joc nou
          </button>
        )}
      </div>
      {renderGrid()}
      {renderKeyboard()}
    </div>
  );
};

function removeDiacritics(str) {
  return str.replace(/[ăâîșț]/g, (match) => {
    switch (match) {
      case 'ă':
        return 'a';
      case 'â':
        return 'a';
      case 'î':
        return 'i';
      case 'ș':
        return 's';
      case 'ț':
        return 't';
      default:
        return match;
    }
  });
}

export default WordleRomanian;
